import type {
  CmmdBoardTask,
  CmmdForgeTaskDetail,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdProjectMilestone,
  UpdateCmmdBoardTaskParams,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
} from "@cmmd-forge/contracts";
import { ProjectTaskAdditionalDetails } from "./ProjectTaskAdditionalDetails";
import { ProjectTaskEditableDetails } from "./ProjectTaskEditableDetails";
import { formatTaskDateTime } from "./taskDateTime";

export interface ProjectTaskDetailsRailProps {
  readonly variant: "mobile" | "desktop";
  readonly task: CmmdBoardTask;
  readonly taskDetail: CmmdForgeTaskDetail | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly pending: boolean;
  readonly statusMutationDisabled: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly onSetStatus: (id: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
  ) => Promise<boolean>;
}

export function ProjectTaskDetailsRail(props: ProjectTaskDetailsRailProps) {
  if (props.variant === "mobile") {
    return (
      <div className="lg:hidden">
        <ProjectTaskDetailFields {...props} />
      </div>
    );
  }
  return (
    <aside
      data-task-workbench-details
      className="hidden min-h-0 border-l border-border bg-muted px-4 py-4 lg:overflow-y-auto lg:overscroll-y-contain lg:block"
    >
      <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Details
      </h2>
      <ProjectTaskDetailFields {...props} />
    </aside>
  );
}

function ProjectTaskDetailFields(props: ProjectTaskDetailsRailProps) {
  return (
    <>
      <ProjectTaskEditableDetails {...props} />

      <ProjectTaskAdditionalDetails detail={props.taskDetail} members={props.members} />

      <dl className="mt-4 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
        <ReadOnlyDetail label="Created" value={formatTaskDateTime(props.task.createdAt)} compact />
        <ReadOnlyDetail label="Updated" value={formatTaskDateTime(props.task.updatedAt)} compact />
      </dl>
    </>
  );
}

function ReadOnlyDetail(props: {
  readonly label: string;
  readonly value: string | null;
  readonly compact?: boolean;
}) {
  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
      <dt className={props.compact ? undefined : "text-xs font-medium text-muted-foreground"}>
        {props.label}
      </dt>
      <dd className="min-w-0 break-words text-foreground">{props.value ?? "None"}</dd>
    </div>
  );
}
